import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { AbService } from '@df/module-ab/ab.service';
import { ColorOptions } from '@df/ui/atomic/atomic-options';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { OptionColorPipe } from '@df/ui/common/option-color.pipe';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import uniq from 'lodash-es/uniq';
import { DialogComponent } from '../../../common/dialog/dialog.component';
import { DialogOpenDirective } from '../../../common/dialog/open/dialog-open.directive';
import { LocalClient } from '../../../module/core/client.decorator';
import { BreadcrumbsComponent } from '../../meta/breadcrumbs.component';
import { NProductViewLayout } from '../product.decorator';
import { ProductRangeContentComponent } from '../range-content/product-range-content.component';
import { ProductSaleBannerComponent } from '../sale-banner/sale-banner';
import {
    ProductViewSeeInStoreModalComponent,
    SEE_IN_STORE_MODAL_ID
} from '../see-in-store/modal/product-view-see-in-store-modal.component';
import { CARE_INFORMATION_MODAL_ID } from '../view-care-information/modal/product-view-care-information-modal.component';
import { DELIVERY_MODAL_ID, ProductViewDeliveryModalComponent } from '../view-delivery/modal/product-view-delivery-modal.component';
import { ProductViewDescriptionComponent } from '../view-description/product-view-description.component';
import { ProductViewDetailsComponent } from '../view-details/product-view-details.component';
import { ProductViewDimensionsAndDetailsModalRightComponent } from '../view-dimensions-and-details/modal/right/product-view-dimensions-and-details-modal-right.component';
import { ProductViewMediaComponent } from '../view-media/product-view-media.component';
import {
    WARRANTY_AND_RETURNS_MODAL_ID,
    WarrantyAndReturnsModalComponent
} from '../view-warranty-and-returns/modal/product-view-warranty-and-returns-modal.component';
import { ProductViewComponent } from './../view/product-view.component';

declare module '@icms/core/content/interfaces/icms-content-component.interfaces' {
    namespace NICmsContentComponent {
        interface IOutletOptions {
            /**
             * Extends outlet options allowim module processing for outlet
             */
            productPageDisplayRules?: boolean;
        }
    }
}

/**
 * Component definition
 */
@Component({
    selector: 'cms-product-view-layout',
    templateUrl: './product-view-layout.html',
    standalone: true,
    imports: [
        BreadcrumbsComponent,
        CommonModule,
        DialogComponent,
        DialogOpenDirective,
        ICmsClientModule,
        IfSizeDirective,
        ProductRangeContentComponent,
        ProductSaleBannerComponent,
        ProductViewDeliveryModalComponent,
        ProductViewDescriptionComponent,
        ProductViewDetailsComponent,
        ProductViewDimensionsAndDetailsModalRightComponent,
        ProductViewMediaComponent,
        ProductViewSeeInStoreModalComponent,
        TranslatePipe,
        WarrantyAndReturnsModalComponent
    ]
})
@ICmsComponent<ProductViewLayoutComponent>({
    id: NProductViewLayout.BLOCK_ID,
    block: {
        url: NProductViewLayout.BLOCK_ID,
        autoload: 'prerender'
    },
    onDemandOnly: true,
    name: 'Product page layout',
    description: 'Product page layout block',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-more-colours',
            title: 'More colours'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'moreColoursCopy',
            title: 'Copy'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-sale-banner',
            title: 'Sale banner'
        },
        <NICmsContentField.IChoiceField>{
            key: 'sale_banner_font_color',
            title: 'Font Color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            options: ColorOptions
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-dimensions-and-details',
            title: 'Dimension and details'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'dimensions_icons',
            title: 'Dimensions Icons',
            itemName: 'Dimension Icon',
            itemNameField: 'dimensions_icons',
            maxSize: 4,
            fields: [
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Icon',
                    required: true
                },
                <NICmsContentField.IRelationField>{
                    key: 'attributes',
                    required: true,
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Select attributes',
                    options: {
                        type: 'attribute',
                        qty: 1,
                        title: 'Attribute'
                    }
                }
            ]
        },
        <NICmsContentField.IRelationField>{
            key: 'dimensionsTopAttributes',
            type: NICmsContentField.EFieldType.relation,
            title: 'Select top in-line attributes',
            description: '"WIll it fit" pdf link is hardcoded, up to 2 extra attributes will be displayed.',
            options: {
                type: 'attribute',
                multiple: true
            }
        },

        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'dimensions_sections',
            title: 'Dimensions Sections',
            itemName: 'Dimension Sections',
            itemNameField: 'dimensions_sections',
            fields: [
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Dimension Sections title',
                    required: true
                },
                <NICmsContentField.IRelationField>{
                    key: 'attributes',
                    required: true,
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Select attributes',
                    options: {
                        type: 'attribute',
                        multiple: true
                    }
                }
            ],
            defaultSize: 1,
            maxSize: 1
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-specification',
            title: 'Specifications for clearance products'
        },
        <NICmsContentField.IRelationField>{
            key: 'attributes',
            required: true,
            type: NICmsContentField.EFieldType.relation,
            title: 'Select attributes',
            options: {
                type: 'attribute'
            }
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-top',
            title: 'Top part'
        },
        <NICmsContentField.IComponentsField>{
            key: 'topComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Top components',
            itemName: 'component',
            default: []
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-bottom',
            title: 'Bottom part'
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IRelationField>{
            key: 'topOutletCategoriesWhitelist',
            type: NICmsContentField.EFieldType.relation,
            title: 'Top Outlet Product Categories Whitelist',
            description: 'Shows PDP top outlet only if currently viewed item or its parent is in at least one of the selected categories.',
            options: {
                type: 'category',
                multiple: true
            }
        }
    ]
})
export class ProductViewLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewLayout.IBlockDataContent;

    @Input()
    variant?: string;

    seeInStoreModalId = SEE_IN_STORE_MODAL_ID;
    warrantyAndReturnsModalId = WARRANTY_AND_RETURNS_MODAL_ID;
    deliveryModalId = DELIVERY_MODAL_ID;
    careInformationModalId = CARE_INFORMATION_MODAL_ID;

    protected override canDetach = false;
    /**
     * Product shorthand
     */
    get product() {
        return this.productView.model;
    }
    /**
     * Parent product shorthand
     */
    get parent() {
        return this.productView.parent;
    }
    /**
     * Request shorthand
     */
    get request() {
        return this.productView.request;
    }
    /**
     * Dependencies
     */
    constructor(
        private afterContentInit: AfterContentInitDirectiveService,
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private ab: AbService
    ) {
        super();
    }

    /**
     * Product view component
     */
    readonly productView = inject(ProductViewComponent);

    override detectChanges() {
        this.client.isCms ? this.productView.detectChanges() : super.detectChanges();
    }

    ngAfterViewInit(): void {
        this.timeoutService.setTimeout(() => this.toggleVisibility(true), 200);
        this.timeoutService.setTimeout(() => this.afterContentInit.unlock(this), 8000);
    }

    override ngOnInit(): void {
        this.toggleVisibility(false);
        this.afterContentInit.lock(this);
        this.ab.getSubject('pdpRedesign').subscribe(() => {
            this.detectChanges();
        });
        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.afterContentInit.unlock(this);
    }

    private toggleVisibility(visible: boolean) {
        this.renderer2.setStyle(this.elementRef.nativeElement, 'visibility', visible ? 'visible' : 'hidden');
    }

    get canShowTopOutlet(): boolean {
        if ((<LocalClient>this.client).isCpc) {
            const productCategories = uniq(this.product.data?.category_ids || []).concat(this.parent?.data?.category_ids || []);
            const whitelistedCategories = this.content?.topOutletCategoriesWhitelist || [];
            return whitelistedCategories?.filter(category => productCategories.includes(category)).length > 0;
        }
        return false;
    }
}
